import { z } from 'zod';
import { configKey, editableText, entityId, publishable } from './primitives.js';
import { listingCard } from './listing.js';

/**
 * BUSCA E FILTROS (Cap. 20)
 *
 * A barra de filtros e gerada a partir de `SearchConfig`. Ligar um filtro
 * novo de "aceita permuta" e marcar `filterable` no campo e publicar — nao
 * ha componente de filtro escrito a mao por atributo.
 */

export const filterControl = z.enum([
  'select',
  'multiselect',
  'range',
  'range_slider',
  'toggle',
  'chips',
  'checkbox_list',
  'text',
  'location',
]);

export const filterDefinition = z.object({
  key: configKey,
  /** Campo do anuncio consultado. Aceita `attributes.x` para campos dinamicos. */
  field: z.string().min(1),
  label: editableText,
  control: filterControl,
  /** Passo/limites para controles de intervalo. */
  range: z
    .object({
      min: z.number().nullable().default(null),
      max: z.number().nullable().default(null),
      step: z.number().default(1),
      /** Atalhos clicaveis: "ate 200 mil", "300 a 500 m2". */
      presets: z
        .array(z.object({ label: editableText, min: z.number().nullable(), max: z.number().nullable() }))
        .default([]),
    })
    .nullable()
    .default(null),
  /** Aparece na barra principal ou so no painel "mais filtros". */
  placement: z.enum(['primary', 'secondary', 'advanced']).default('secondary'),
  /** Mostra contagem de resultados por opcao. */
  showCounts: z.boolean().default(true),
  publication: publishable.default({}),
});

export type FilterDefinition = z.infer<typeof filterDefinition>;

export const sortOption = z.object({
  key: configKey,
  label: editableText,
  field: z.string().min(1),
  direction: z.enum(['asc', 'desc']).default('desc'),
  isDefault: z.boolean().default(false),
  publication: publishable.default({}),
});

export type SortOption = z.infer<typeof sortOption>;

export const searchConfig = z.object({
  version: z.string(),
  filters: z.array(filterDefinition).default([]),
  sorts: z.array(sortOption).default([]),
  pageSize: z.number().int().min(6).max(96).default(24),
  /**
   * Composicao do ranking. Cada peso e editavel no painel — e assim que o
   * "destaque" do plano influencia a vitrine sem codigo (Cap. 16.6).
   */
  ranking: z
    .object({
      planBoostWeight: z.number().default(1),
      recencyWeight: z.number().default(1),
      completenessWeight: z.number().default(0.5),
      photoCountWeight: z.number().default(0.3),
      relevanceWeight: z.number().default(2),
      /** Quantos destaques pagos podem ocupar o topo antes do organico. */
      maxPinnedPerPage: z.number().int().min(0).default(3),
    })
    .default({}),
  /** "QUAL IMOVEL DESEJA?" — busca em linguagem natural (Cap. 5.2). */
  naturalLanguage: z
    .object({
      enabled: z.boolean().default(true),
      placeholder: editableText.default('Ex.: lote em Itauna, 300 a 500 m2, ate R$ 180 mil, plano e com matricula'),
      /** Guarda a frase como uma "procura" para gerar match (Cap. 12). */
      persistAsSavedSearch: z.boolean().default(true),
      /** Pede login antes de salvar a procura. */
      requiresAuthToPersist: z.boolean().default(false),
      examples: z.array(editableText).default([]),
    })
    .default({}),
});

export type SearchConfig = z.infer<typeof searchConfig>;

// --- runtime: consulta e resultado ---

export const searchQuery = z.object({
  /** Texto livre ou frase em linguagem natural. */
  q: z.string().nullable().default(null),
  /** Valores por chave de filtro. Range vira { min, max }. */
  filters: z.record(z.string(), z.unknown()).default({}),
  sort: z.string().nullable().default(null),
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(96).default(24),
  /** Busca por proximidade geografica. */
  near: z
    .object({ latitude: z.number(), longitude: z.number(), radiusKm: z.number().min(0.1).max(500) })
    .nullable()
    .default(null),
});

export type SearchQuery = z.infer<typeof searchQuery>;

/**
 * Interpretacao da frase em linguagem natural, devolvida junto com o
 * resultado para que a UI mostre "entendi: Itauna, 300-500 m2, ate 180 mil"
 * com chips removiveis.
 */
export const parsedIntent = z.object({
  raw: z.string(),
  filters: z.record(z.string(), z.unknown()).default({}),
  /** Confianca por chave interpretada (0-1). Abaixo do limiar, a UI pergunta. */
  confidence: z.record(z.string(), z.number()).default({}),
  unresolved: z.array(z.string()).default([]),
});

export type ParsedIntent = z.infer<typeof parsedIntent>;

export const facetBucket = z.object({ value: z.string(), label: z.string(), count: z.number().int().min(0) });

export const searchResult = z.object({
  items: z.array(listingCard).default([]),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  facets: z.record(z.string(), z.array(facetBucket)).default({}),
  intent: parsedIntent.nullable().default(null),
  /** Id da procura salva, quando a consulta virou alerta/match. */
  savedSearchId: entityId.nullable().default(null),
  tookMs: z.number().int().min(0).default(0),
});

export type SearchResult = z.infer<typeof searchResult>;
